import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { useTheme } from '@mui/material/styles';
import { format, parseISO, isValid } from 'date-fns';
import React, {
  FC, useLayoutEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { OrderNotesTypes } from '../types';
import { useUserInformationContext } from 'contexts/userContext';
import { sanitizeNoteHtml } from '../utils/sanitizeNoteHtml';

const useStyles = makeStyles((theme) =>
  createStyles({
    container: {
      borderRadius: '6px',
      display: 'flex',
      flexDirection: 'column',
    },
    text: {
      color: theme.palette.primary.dark,
    },
    textLight: {
      color: theme.palette.primary.contrastText,
    },
    header: {
      backgroundColor: theme.palette.info.light,
      padding: theme.spacing(1),
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        flexWrap: 'wrap',
      },
    },
    headerContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      minWidth: 0,
      flex: 1,
    },
    headerText: {
      overflowWrap: 'anywhere',
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
    },
    actionButton: {
      minWidth: 44,
      minHeight: 44,
    },
    containerTitle: { gap: theme.spacing(1) },
    critical: {
      backgroundColor: theme.palette.error.dark,
    },
    resolved: {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
    },
    body: {
      padding: theme.spacing(1),
      fontSize: '0.875rem',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      '&:last-child': {
        paddingBottom: theme.spacing(1),
      },
      '& p:first-child, & ul:first-child, & ol:first-child': {
        marginTop: 0,
      },
      '& p:last-child, & ul:last-child, & ol:last-child': {
        marginBottom: 0,
      },
    },
    bodyContent: {
      display: '-webkit-box',
      WebkitLineClamp: 5,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    readMoreButton: {
      textTransform: 'none',
      minWidth: 0,
      padding: theme.spacing(0.5, 0),
      marginTop: theme.spacing(0.5),
    },
    readMoreContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: 'auto',
    },
  })
);

interface NoteProps {
	note: OrderNotesTypes;
	handleToogleOpen?: (note) => void;
	handleToogleDelete?: (note) => void;
}

const Note: FC<NoteProps> = ({ note, handleToogleOpen, handleToogleDelete }) => {
  const { currentUser } = useUserInformationContext();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const contentRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el || expanded) return;
    setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [note.content, expanded, isMobile]);

  const formatDate = (date) => {
    if (!date || !isValid(parseISO(String(date)))) return '';
    const pattern = isMobile ? 'yyyy/MM/dd HH:mm' : 'd MMMM, yyyy HH:mm';
    return format(parseISO(String(date)), pattern);
  };

  const isAuthor = currentUser?._id === note.party_id;
  const isLocked = note.critical && note.resolved;
  const canEdit = Boolean(handleToogleOpen) && isAuthor && !isLocked;
  const canDelete = Boolean(handleToogleDelete) && isAuthor && !isLocked;

  const classes = useStyles();
  const headerTextClass = note.critical && !note.resolved ? classes.textLight : classes.text;

  return (
    <Card className={classes.container}>
      <Box
        className={clsx({
				  [classes.header]: true,
				  [classes.critical]: note.critical && !note.resolved,
				  [classes.resolved]: note.critical && note.resolved,
        })}
      >
        <Box className={classes.headerContent}>
          <Box className={classes.headerText}>
            <Typography variant="subtitle2" className={headerTextClass}>
              {t('orders:note-by', { name: note.name })}
            </Typography>
            <Typography variant="subtitle2" className={headerTextClass}>
              {t('orders:note-on', { date: formatDate(note.created) })}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.actions}>
          {canDelete && (
          <IconButton
            disableTouchRipple
            className={classes.actionButton}
            onClick={() => handleToogleDelete?.(note._id)}
            aria-label={t('orders:deleteNoteAria')}
            size="large"
          >
            <FontAwesomeIcon icon="trash" size="sm" className={headerTextClass} />
          </IconButton>
          )}
          {canEdit && (
          <IconButton
            disableTouchRipple
            className={classes.actionButton}
            onClick={() => handleToogleOpen?.(note)}
            aria-label={t('orders:editNoteAria')}
            size="large"
          >
            <FontAwesomeIcon icon="pen" size="sm" className={headerTextClass} />
          </IconButton>
          )}
        </Box>
      </Box>
      <CardContent className={classes.body}>
        {/* eslint-disable react/no-danger -- sanitized by sanitizeNoteHtml */}
        <div
          ref={contentRef}
          className={clsx({ [classes.bodyContent]: !expanded })}
          dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(note.content) }}
        />
        {/* eslint-enable react/no-danger */}
        {isOverflowing && (
        <Box className={classes.readMoreContainer}>
          <Button
            size="small"
            className={classes.readMoreButton}
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? t('orders:readLess') : t('orders:readMore')}
          </Button>
        </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default Note;
